import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { useCreateUser } from '../hooks/useCreateUser';
import { useSignInUser } from '../hooks/useSignInUser';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import { api } from '../services/api';
import ShimmerButton from './ShimmerButton';
import GlowingContainer from './GlowingContainer';
import { PolicyModal } from './PolicyModal';
import { useToast } from '../contexts/ToastContext';
import { getApiBaseUrl } from '../utils/apiConfig';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsEvents } from '../utils/analytics';
import { useCampaign } from '../contexts/CampaignContext';

interface PairFormSocialProps {
    deviceId: string;
    deviceCode: string;
    fromCampaign?: boolean;
    shouldDisableAutoFocus?: boolean;
    onSuccess: (
        email: string,
        userId: string,
        userData?: any,
        isNewUser?: boolean
    ) => void;
}

declare global {
    interface Window {
        AppleID?: any;
    }
}

export default function PairFormSocial({
    deviceId,
    deviceCode,
    fromCampaign = false,
    shouldDisableAutoFocus = false,
    onSuccess,
}: PairFormSocialProps) {
    const analytics = useAnalytics();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPasswordField, setShowPasswordField] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userExists, setUserExists] = useState(true); // Default to true, will be determined after first submit
    const [showSocialButtons, setShowSocialButtons] = useState(true); // Re-enabled for design review
    const [isLoading, setIsLoading] = useState(false);
    const [reauthMessage, setReauthMessage] = useState<string | null>(null);
    const [isAppleLoading, setIsAppleLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [googleProfile, setGoogleProfile] = useState<any>(null);
    const [googleTicket, setGoogleTicket] = useState<string>('');
    const [termsAccepted, setTermsAccepted] = useState(true); // Default to true as per design
    const [policyModal, setPolicyModal] = useState<{
        isOpen: boolean;
        title: string;
        url: string;
    }>({
        isOpen: false,
        title: '',
        url: '',
    });
    const createUser = useCreateUser();
    const signInUser = useSignInUser();
    const { showToast } = useToast();
    const router = useRouter();
    const emailInputRef = useRef<HTMLInputElement>(null);
    const { couponError } = useCampaign();

    const { googleLogin } = useGoogleAuth({
        onSuccess: async (response) => {
            setIsGoogleLoading(false);
            analytics.trackAuthEvent('method_selected', 'google');

            if (response.action === 'signin' && response.result.success) {
                // Existing user signed in successfully
                console.log('[PAIR-APP] Google sign-in response structure:', {
                    hasResult: !!response.result,
                    resultSuccess: response.result?.success,
                    hasData: !!response.result?.data,
                    dataKeys: response.result?.data
                        ? Object.keys(response.result.data)
                        : [],
                    hasToken: !!response.result?.data?.token,
                    tokenLength: response.result?.data?.token
                        ? response.result.data.token.length
                        : 0,
                });
                console.log(
                    '[PAIR-APP] Full response.result.data:',
                    response.result.data
                );

                // Store the JWT token in the API instance
                if (response.result.data && response.result.data.token) {
                    api.setJwtToken(response.result.data.token);
                    console.log(
                        '[PAIR-APP] JWT token set successfully, length:',
                        response.result.data.token.length
                    );
                    // Store auth method for silent refresh
                    localStorage.setItem('authMethod', 'google');
                    localStorage.setItem(
                        'authTimestamp',
                        Date.now().toString()
                    );
                } else {
                    console.error(
                        '[PAIR-APP] ⚠️ NO JWT TOKEN IN GOOGLE SIGN-IN RESPONSE!'
                    );
                    console.error(
                        '[PAIR-APP] This will cause authentication to fail on subsequent requests'
                    );
                }
                analytics.trackAuthEvent('succeeded', 'google', {
                    is_new_user: false,
                });
                analytics.identifyUser(response.result.data.id, {
                    email: response.result.data.email,
                });
                onSuccess(
                    response.result.data.email,
                    response.result.data.id,
                    response.result.data,
                    false
                );
            } else if (
                response.action === 'signup' &&
                response.profile.success
            ) {
                // New user - automatically create account
                const profileData = response.profile.data;
                console.log(
                    '[PAIR-APP] Creating new user from Google profile:',
                    profileData.email
                );
                setIsGoogleLoading(true); // Keep loading state during account creation

                try {
                    // Generate a random password that we'll use for both signup and signin
                    const randomPassword = Math.random()
                        .toString(36)
                        .slice(-12);

                    // Create user with Google profile data
                    const signupData: any = {
                        email: profileData.email,
                        password: randomPassword,
                        deviceId,
                        deviceCode,
                        socialProvider: 'google',
                        socialTicket: profileData.ticket,
                        // Pass names from Google profile if available
                        firstName:
                            profileData.given_name ||
                            profileData.name?.split(' ')[0],
                        lastName:
                            profileData.family_name ||
                            profileData.name?.split(' ')[1],
                    };

                    const signupResult = await createUser.mutateAsync(
                        signupData
                    );

                    if (signupResult.__isApiError) {
                        // Check if the error is because email already exists
                        if (
                            signupResult.error?.includes('email already exists')
                        ) {
                            console.log(
                                '[PAIR-APP] Email exists - user needs to sign in with password'
                            );

                            // Pre-fill the email field and show password field
                            setEmail(profileData.email);
                            setShowPasswordField(true);
                            setUserExists(true);
                            setShowSocialButtons(false);

                            showToast(
                                'This email is already registered. Please enter your password to sign in.',
                                'info'
                            );
                            setIsGoogleLoading(false);
                            return;
                        }

                        // Other signup errors
                        showToast(
                            signupResult.error || 'Failed to create account',
                            'error'
                        );
                        setIsGoogleLoading(false);
                        return;
                    }

                    // Account created successfully - now sign them in to get JWT
                    if (signupResult.data) {
                        console.log(
                            '[PAIR-APP] Google account created, signing in to get JWT...'
                        );

                        try {
                            // Sign in with email/password to get JWT token
                            const signinResult = await signInUser.mutateAsync({
                                email: profileData.email,
                                password: randomPassword,
                            });

                            if (
                                !signinResult.__isApiError &&
                                signinResult.data
                            ) {
                                console.log(
                                    '[PAIR-APP] Google user signed in successfully with JWT'
                                );
                                // Store the JWT token in the API instance
                                if (signinResult.data.token) {
                                    api.setJwtToken(signinResult.data.token);
                                    // Store auth method for silent refresh (new user via Google)
                                    localStorage.setItem(
                                        'authMethod',
                                        'google'
                                    );
                                    localStorage.setItem(
                                        'authTimestamp',
                                        Date.now().toString()
                                    );
                                }
                                analytics.trackAuthEvent(
                                    'succeeded',
                                    'google',
                                    { is_new_user: true }
                                );
                                analytics.identifyUser(signinResult.data.id, {
                                    email: signinResult.data.email,
                                });
                                onSuccess(
                                    signinResult.data.email,
                                    signinResult.data.id,
                                    signinResult.data,
                                    true
                                ); // Mark as new user
                            } else {
                                // If signin fails, still proceed but without JWT
                                console.warn(
                                    '[PAIR-APP] Sign-in after signup failed, proceeding without JWT'
                                );
                                const userId = signupResult.data.id;
                                onSuccess(
                                    profileData.email,
                                    userId,
                                    signupResult.data,
                                    true
                                );
                            }
                        } catch (signinError) {
                            console.error(
                                'Failed to sign in after Google signup:',
                                signinError
                            );
                            // Proceed without JWT
                            const userId = signupResult.data.id;
                            onSuccess(
                                profileData.email,
                                userId,
                                signupResult.data,
                                true
                            );
                        }
                    } else {
                        showToast(
                            'Account created but user data not found',
                            'error'
                        );
                    }
                } catch (error) {
                    console.error('Failed to create Google user:', error);
                    showToast('Failed to create account with Google', 'error');
                    setIsGoogleLoading(false);
                }
            }
        },
        onError: (error) => {
            setIsGoogleLoading(false);
            console.error('Google sign-in error:', error);
            analytics.trackAuthEvent('failed', 'google', {
                error_message: String(error),
            });
        },
    });

    // Check for re-authentication email on mount
    useEffect(() => {
        const reauthEmail = localStorage.getItem('reauth_email');
        if (reauthEmail) {
            console.log(
                '[PAIR-APP] Found re-auth email, pre-filling:',
                reauthEmail
            );
            setEmail(reauthEmail);
            setShowPasswordField(true); // Show password field immediately for re-auth
            setUserExists(true); // We know user exists if re-authenticating

            // Check if we have a session expired message from coupon error
            if (couponError && couponError.includes('session')) {
                setReauthMessage(
                    'Your session has expired. Please sign in again to continue.'
                );
            }

            // Clear the stored email after using it
            localStorage.removeItem('reauth_email');
        }
    }, [couponError]);

    // Removed auto-focus for email input as per user request
    // The email field will no longer automatically focus after page load

    // Monitor email input to show/hide social buttons
    useEffect(() => {
        if (email.trim() === '') {
            setShowSocialButtons(true);
            setShowPasswordField(false);
            setUserExists(true); // Reset to default
        } else {
            setShowSocialButtons(false);
        }
    }, [email]);

    // Load Apple Sign In SDK
    useEffect(() => {
        const script = document.createElement('script');
        script.src =
            'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        script.onload = () => {
            if (window.AppleID) {
                // Always use window.location.origin for consistency across all environments
                // Using V1 endpoint (ExternalAuthRedirect) to match main app behavior
                const redirectURI = `${window.location.origin}/Server/users/ExternalAuthRedirect`;

                console.log(
                    '[PAIR-APP] Apple Sign In redirect URI (V1):',
                    redirectURI
                );
                console.log(
                    '[PAIR-APP] NOTE: Make sure this URL is added to Apple Developer Portal'
                );

                window.AppleID.auth.init({
                    clientId: 'com.niio.web',
                    scope: 'email',
                    redirectURI: redirectURI,
                    usePopup: true,
                });
            }
        };

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const handleGoogleSignIn = () => {
        console.log('[PAIR-APP] handleGoogleSignIn called');
        console.log('[PAIR-APP] Current URL:', window.location.href);
        console.log('[PAIR-APP] Origin:', window.location.origin);
        setIsGoogleLoading(true);
        googleLogin();
    };

    const handleAppleSignIn = async () => {
        if (!window.AppleID) {
            showToast('Apple Sign-In is not loaded', 'error');
            return;
        }

        try {
            setIsAppleLoading(true);
            const response = await window.AppleID.auth.signIn();

            if (response.authorization && response.authorization.code) {
                let userInfo: any = {};

                // Decode ID token if available
                if (response.authorization.id_token) {
                    const base64Url =
                        response.authorization.id_token.split('.')[1];
                    const base64 = base64Url
                        .replace(/-/g, '+')
                        .replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(
                        atob(base64)
                            .split('')
                            .map(function (c) {
                                return (
                                    '%' +
                                    ('00' + c.charCodeAt(0).toString(16)).slice(
                                        -2
                                    )
                                );
                            })
                            .join('')
                    );
                    const decodedToken = JSON.parse(jsonPayload);
                    userInfo = {
                        oid: decodedToken.sub,
                        email: decodedToken.email,
                    };
                }

                // Check if user data is available from response
                if (response.user) {
                    const { email, name } = response.user;
                    if (email) userInfo.email = email;
                    if (name) {
                        userInfo.firstName = name.firstName;
                        userInfo.lastName = name.lastName;
                    }
                }

                // Try to sign in with the code and user info using V1 endpoint (matches main app)
                // Pass allowSignup: true to create SocialProfile if user doesn't exist
                console.log(
                    '[PAIR-APP] Calling socialSignIn for Apple with allowSignup...'
                );
                console.log('[PAIR-APP] Parameters being passed:', {
                    provider: 'apple',
                    code: response.authorization.code,
                    userInfo: userInfo,
                    allowSignup: true,
                });
                const result = await api.socialSignIn(
                    'apple',
                    response.authorization.code,
                    userInfo,
                    true // allowSignup - creates SocialProfile if user doesn't exist
                );

                if (result.success && result.data) {
                    // Successfully signed in existing user
                    console.log(
                        '[PAIR-APP] Apple sign in successful:',
                        result.data
                    );

                    // Set JWT token if available
                    if (result.data.token) {
                        api.setJwtToken(result.data.token);
                        // Store auth method for silent refresh (Apple signin)
                        localStorage.setItem('authMethod', 'apple');
                        localStorage.setItem(
                            'authTimestamp',
                            Date.now().toString()
                        );
                    }

                    // Track successful auth
                    analytics.trackAuthEvent('success', 'apple', {
                        is_new_user: false,
                    });

                    onSuccess(
                        result.data.email,
                        result.data.id,
                        result.data,
                        false
                    );
                } else if (
                    !result.success &&
                    (result.requiresSignup ||
                        result.error?.includes('No existing account') ||
                        result.error?.includes('User not found') ||
                        result.error?.includes('does not exist'))
                ) {
                    // User doesn't exist, need to create account
                    // V1 endpoint might not return requiresSignup flag, so we check error messages
                    console.log(
                        '[PAIR-APP] Apple user needs signup, creating account...'
                    );

                    // Use profile from result if available, otherwise use userInfo we collected
                    const profileData = result.profile || userInfo;

                    // Make sure we have an email
                    if (!profileData.email) {
                        console.error(
                            '[PAIR-APP] No email available for Apple signup'
                        );
                        showToast(
                            'Unable to get email from Apple Sign-in',
                            'error'
                        );
                        setIsAppleLoading(false);
                        return;
                    }

                    // Generate a random password for the account (required by API)
                    const randomPassword = Math.random()
                        .toString(36)
                        .slice(-12);

                    // Use the ticket returned from the backend (it contains the SocialProfile reference)
                    // The backend already created the SocialProfile when we called socialSignIn with redirect:true
                    // IMPORTANT: We use socialTicket (not socialCode) to link with the existing SocialProfile
                    const ticket = result.ticket;
                    const appleOid =
                        userInfo.oid ||
                        profileData.oid ||
                        profileData.id ||
                        profileData.sub;

                    // Handle anonymous Apple users who don't share their name
                    // Check if email is Apple's private relay (format: xxx@privaterelay.appleid.com)
                    const isPrivateRelay = profileData.email?.includes(
                        '@privaterelay.appleid.com'
                    );
                    let firstName = profileData.firstName || userInfo.firstName;
                    let lastName = profileData.lastName || userInfo.lastName;

                    // If no name provided or using private relay, use "Art Enthusiast" as default
                    if (!firstName && !lastName) {
                        firstName = 'Art';
                        lastName = 'Enthusiast';
                        console.log(
                            '[PAIR-APP] Using default name for anonymous Apple user'
                        );
                    } else if (
                        isPrivateRelay &&
                        firstName &&
                        firstName.includes('@')
                    ) {
                        // Sometimes the email gets incorrectly parsed as firstName
                        firstName = 'Art';
                        lastName = 'Enthusiast';
                        console.log(
                            '[PAIR-APP] Replacing private relay email used as name with default'
                        );
                    }

                    const signupData: any = {
                        email: profileData.email,
                        password: randomPassword, // Required for account creation
                        deviceId,
                        deviceCode,
                        socialProvider: 'apple',
                        socialTicket: ticket, // The ticket that references the created SocialProfile
                        socialOid: appleOid, // The Apple user ID (OID)
                        // Pass names - either from Apple or defaults
                        firstName,
                        lastName,
                    };

                    // Log what we're sending for debugging
                    console.log('[PAIR-APP] Apple signup data:', {
                        email: signupData.email,
                        socialProvider: signupData.socialProvider,
                        socialTicket: signupData.socialTicket,
                        socialOid: signupData.socialOid,
                        firstName: signupData.firstName,
                        lastName: signupData.lastName,
                        isPrivateRelay,
                    });

                    try {
                        const signupResult = await createUser.mutateAsync(
                            signupData
                        );

                        if (signupResult.__isApiError) {
                            // Check if the error is because email already exists
                            if (
                                signupResult.error?.includes(
                                    'email already exists'
                                )
                            ) {
                                console.log(
                                    '[PAIR-APP] Email exists - user needs to sign in with password'
                                );

                                // Pre-fill the email field and show password field
                                setEmail(profileData.email);
                                setShowPasswordField(true);
                                setUserExists(true);
                                setShowSocialButtons(false);

                                showToast(
                                    'This email is already registered. Please enter your password to sign in.',
                                    'info'
                                );
                                setIsAppleLoading(false);
                                return;
                            }

                            // Other signup errors
                            showToast(
                                signupResult.error ||
                                    'Failed to create account',
                                'error'
                            );
                            setIsAppleLoading(false);
                            return;
                        }

                        // Account created successfully - now sign them in to get JWT
                        if (signupResult.data) {
                            console.log(
                                '[PAIR-APP] Apple account created, signing in to get JWT...'
                            );

                            try {
                                // Sign in with email/password to get JWT token
                                const signinResult =
                                    await signInUser.mutateAsync({
                                        email: profileData.email,
                                        password: randomPassword,
                                    });

                                if (
                                    !signinResult.__isApiError &&
                                    signinResult.data
                                ) {
                                    console.log(
                                        '[PAIR-APP] Apple user signed in successfully with JWT'
                                    );
                                    // Store the JWT token in the API instance
                                    if (signinResult.data.token) {
                                        api.setJwtToken(
                                            signinResult.data.token
                                        );
                                        // Store auth method for silent refresh (new user via Apple)
                                        localStorage.setItem(
                                            'authMethod',
                                            'apple'
                                        );
                                        localStorage.setItem(
                                            'authTimestamp',
                                            Date.now().toString()
                                        );
                                    }

                                    // Track successful auth
                                    analytics.trackAuthEvent(
                                        'succeeded',
                                        'apple',
                                        {
                                            is_new_user: true,
                                        }
                                    );

                                    onSuccess(
                                        profileData.email,
                                        signinResult.data.id,
                                        signinResult.data,
                                        true
                                    );
                                } else {
                                    showToast(
                                        'Account created but failed to sign in',
                                        'error'
                                    );
                                }
                            } catch (signinError) {
                                console.error(
                                    'Failed to sign in after Apple account creation:',
                                    signinError
                                );
                                showToast(
                                    'Account created but failed to sign in',
                                    'error'
                                );
                            }
                        }
                    } catch (error) {
                        console.error('Failed to create Apple user:', error);
                        showToast(
                            'Failed to create account with Apple',
                            'error'
                        );
                        setIsAppleLoading(false);
                    }
                } else {
                    showToast(
                        result.error || 'Failed to sign in with Apple',
                        'error'
                    );
                }
            }
        } catch (error) {
            console.error('Apple sign-in error:', error);
            showToast('Failed to sign in with Apple', 'error');
        } finally {
            setIsAppleLoading(false);
        }
    };

    const openPolicyModal = (title: string, url: string) => {
        setPolicyModal({ isOpen: true, title, url });
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const startTime = Date.now();

        // Only track AUTH_STARTED when actually attempting authentication (with password)
        // Skip tracking for initial email check to reduce noise
        if (showPasswordField) {
            analytics.trackEvent(AnalyticsEvents.AUTH_STARTED, {
                method: 'email',
                step: userExists ? 'signin_attempt' : 'signup_attempt',
                has_device_params: !!(deviceId && deviceCode),
                email_domain: email.split('@')[1] || '',
            });
        }

        // First submission with just email - check if user exists
        if (!showPasswordField && email.trim() !== '') {
            setIsLoading(true);

            try {
                // Don't track email validation to reduce noise
                // This is just checking if user exists, not actual auth

                // Try to sign in with just email to check if user exists
                const checkResult = await signInUser.mutateAsync({
                    email,
                    password: '',
                });

                if (checkResult.__isApiError) {
                    if (
                        checkResult.status === 500 &&
                        checkResult.error &&
                        checkResult.error.includes("User doesn't exist")
                    ) {
                        // User doesn't exist - show create password flow
                        setUserExists(false);

                        // Don't track new user detected yet - wait for actual signup
                        console.log(
                            '[PAIR-APP] New user detected, showing signup flow'
                        );
                    } else {
                        // User exists (or other error) - show login flow
                        setUserExists(true);

                        // Don't track existing user detected to reduce noise
                        console.log(
                            '[PAIR-APP] Existing user detected, showing signin flow'
                        );
                    }
                }
            } catch (error) {
                // Default to login flow on error
                setUserExists(true);

                // Track (non-blocking) validation error
                analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                    method: 'email_validation',
                    error_type: 'validation_error',
                    error_message:
                        error instanceof Error ? error.message : String(error),
                    email_domain: email.split('@')[1] || '',
                    processing_time: Date.now() - startTime,
                });
            }

            setShowPasswordField(true);
            setIsLoading(false);
            return;
        }

        // Only proceed if we have both email and password
        if (!email || !password) {
            analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                method: 'email',
                error_type: 'incomplete_form',
                error_message: 'Missing email or password',
                has_email: !!email,
                has_password: !!password,
            });
            return;
        }

        setIsLoading(true);

        if (userExists) {
            // User exists - try to sign in
            try {
                // Don't track signin attempt - AUTH_STARTED already tracked above
                console.log('[PAIR-APP] Attempting signin for:', email);

                const signinResult = await signInUser.mutateAsync({
                    email,
                    password,
                });

                if (signinResult.__isApiError) {
                    // Track (non-blocking) sign in failure
                    analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                        method: 'email_signin',
                        error_type: 'signin_error',
                        error_message:
                            signinResult.error || 'Failed to sign in',
                        email_domain: email.split('@')[1] || '',
                        processing_time: Date.now() - startTime,
                    });

                    showToast(
                        signinResult.error || 'Failed to sign in',
                        'error'
                    );
                    setIsLoading(false);
                    return;
                }

                if (signinResult.data) {
                    // Track (non-blocking) successful sign in
                    analytics.trackEvent(AnalyticsEvents.AUTH_SUCCEEDED, {
                        method: 'email_signin',
                        user_id: signinResult.data.id,
                        email_domain: email.split('@')[1] || '',
                        has_device_params: !!(deviceId && deviceCode),
                        processing_time: Date.now() - startTime,
                        is_new_user: false,
                    });

                    analytics.identifyUser(signinResult.data.id, {
                        email: signinResult.data.email,
                        signin_method: 'email',
                    });

                    onSuccess(
                        email,
                        signinResult.data.id,
                        signinResult.data,
                        false
                    );
                } else {
                    // Track (non-blocking) data missing error
                    analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                        method: 'email_signin',
                        error_type: 'data_missing',
                        error_message:
                            'Sign in successful but user data not found',
                        email_domain: email.split('@')[1] || '',
                        processing_time: Date.now() - startTime,
                    });

                    showToast(
                        'Sign in successful but user data not found',
                        'error'
                    );
                }
            } catch (error) {
                console.error('Sign in error:', error);

                // Track (non-blocking) sign in exception
                analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                    method: 'email_signin',
                    error_type: 'signin_exception',
                    error_message:
                        error instanceof Error ? error.message : String(error),
                    email_domain: email.split('@')[1] || '',
                    processing_time: Date.now() - startTime,
                });

                showToast('Failed to sign in', 'error');
            }
        } else {
            // User doesn't exist - try to create account
            try {
                // Don't track signup attempt - will track success/failure only
                console.log('[PAIR-APP] Attempting signup for:', email);

                // Prepare signup data
                const signupData: any = {
                    email,
                    password,
                    deviceId,
                    deviceCode,
                };

                // If this is a Google signup, include the social data
                if (googleProfile && googleTicket) {
                    // Check if we have a V2 ticket or V1 code
                    if (googleProfile.ticket) {
                        // V2 flow - use ticket
                        signupData.socialTicket = googleProfile.ticket;
                        signupData.socialProvider = 'google';
                    } else {
                        // V1 flow - use code
                        signupData.socialCode = googleTicket;
                        signupData.socialProvider = 'google';
                    }
                }

                const signupResult = await createUser.mutateAsync(signupData);

                if (signupResult.__isApiError) {
                    // Track (non-blocking) signup failure
                    analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                        method: 'email_signup',
                        error_type: 'signup_error',
                        error_message:
                            signupResult.error || 'Failed to create account',
                        email_domain: email.split('@')[1] || '',
                        processing_time: Date.now() - startTime,
                        has_google_profile: !!(googleProfile && googleTicket),
                    });

                    showToast(
                        signupResult.error || 'Failed to create account',
                        'error'
                    );
                    setIsLoading(false);
                    return;
                }

                // Signup successful
                if (signupResult.data) {
                    const userId = signupResult.data.id;

                    // Track (non-blocking) successful signup
                    analytics.trackEvent(AnalyticsEvents.AUTH_SUCCEEDED, {
                        method: 'email_signup',
                        user_id: userId,
                        email_domain: email.split('@')[1] || '',
                        has_device_params: !!(deviceId && deviceCode),
                        processing_time: Date.now() - startTime,
                        is_new_user: true,
                        has_google_profile: !!(googleProfile && googleTicket),
                        social_provider: googleProfile ? 'google' : null,
                    });

                    analytics.identifyUser(userId, {
                        email: signupResult.data.email,
                        signup_method: 'email',
                        is_new_user: true,
                        social_provider: googleProfile ? 'google' : null,
                    });

                    onSuccess(email, userId, signupResult.data, true); // Mark as new user
                } else {
                    // Track (non-blocking) data missing error
                    analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                        method: 'email_signup',
                        error_type: 'data_missing',
                        error_message:
                            'Account created but user data not found',
                        email_domain: email.split('@')[1] || '',
                        processing_time: Date.now() - startTime,
                    });

                    showToast(
                        'Account created but user data not found',
                        'error'
                    );
                }
            } catch (error) {
                console.error('Create user error:', error);

                // Track (non-blocking) signup exception
                analytics.trackEvent(AnalyticsEvents.AUTH_FAILED, {
                    method: 'email_signup',
                    error_type: 'signup_exception',
                    error_message:
                        error instanceof Error ? error.message : String(error),
                    email_domain: email.split('@')[1] || '',
                    processing_time: Date.now() - startTime,
                    has_google_profile: !!(googleProfile && googleTicket),
                });

                showToast('Failed to create account', 'error');
            }
        }

        setIsLoading(false);
    };

    return (
        <div className="w-full">
            {/* Show re-authentication message if present */}
            {reauthMessage && (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-sm text-yellow-300 text-center">
                        {reauthMessage}
                    </p>
                </div>
            )}

            <div className="w-full space-y-4 max-w-sm mx-auto">
                <>
                    {/* Google Sign In button */}
                    <AnimatePresence>
                        {showSocialButtons && (
                            <motion.div
                                key="social-buttons"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{
                                    duration: 0.3,
                                    ease: 'easeInOut',
                                }}
                                className="space-y-4 overflow-hidden"
                            >
                                <button
                                    type="button"
                                    onClick={handleGoogleSignIn}
                                    disabled={isAppleLoading || isGoogleLoading}
                                    className="w-full flex items-center justify-center gap-3 px-4 py-3 
                                    bg-white text-gray-800 rounded-lg hover:bg-gray-100 
                                    transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isGoogleLoading ? (
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{
                                                duration: 1,
                                                repeat: Infinity,
                                                ease: 'linear',
                                            }}
                                            className="w-5 h-5 border-2 border-gray-800 border-t-transparent rounded-full"
                                        />
                                    ) : (
                                        <svg
                                            className="w-5 h-5"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                fill="#4285F4"
                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            />
                                            <path
                                                fill="#34A853"
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            />
                                            <path
                                                fill="#FBBC05"
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            />
                                            <path
                                                fill="#EA4335"
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            />
                                        </svg>
                                    )}
                                    {isGoogleLoading
                                        ? 'Signing in...'
                                        : 'Continue with Google'}
                                </button>

                                {/* Apple Sign In button */}
                                <button
                                    type="button"
                                    onClick={handleAppleSignIn}
                                    disabled={isAppleLoading || isGoogleLoading}
                                    className="w-full flex items-center justify-center gap-3 px-4 py-3 
                                    bg-black text-white rounded-lg hover:bg-gray-900 
                                    transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed
                                    border border-white/20"
                                >
                                    {isAppleLoading ? (
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{
                                                duration: 1,
                                                repeat: Infinity,
                                                ease: 'linear',
                                            }}
                                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                                        />
                                    ) : (
                                        <svg
                                            className="w-5 h-5"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.2 12.37 21.2C10.84 21.2 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                                        </svg>
                                    )}
                                    {isAppleLoading
                                        ? 'Signing in...'
                                        : 'Continue with Apple'}
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {showSocialButtons && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="relative my-6"
                            >
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-600"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-[#1a1a2e] text-gray-400">
                                        OR
                                    </span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={handleEmailSubmit} className="space-y-4">
                        <input
                            ref={emailInputRef}
                            type="email"
                            value={email}
                            tabIndex={0}
                            autoComplete="email"
                            onChange={(e) => {
                                const newEmail = e.target.value;
                                setEmail(newEmail);

                                // Reset password field if user modifies email after password field is shown
                                if (showPasswordField) {
                                    setShowPasswordField(false);
                                    setPassword('');
                                    setUserExists(true); // Reset to default
                                    setShowSocialButtons(true); // Show social buttons again
                                }
                            }}
                            onInput={(e) => {
                                // Also handle onInput event for better compatibility
                                const target = e.target as HTMLInputElement;
                                const newEmail = target.value;

                                if (newEmail !== email && showPasswordField) {
                                    setEmail(newEmail);
                                    setShowPasswordField(false);
                                    setPassword('');
                                    setUserExists(true);
                                    setShowSocialButtons(true);
                                }
                            }}
                            placeholder="Continue with Email"
                            className="w-full px-4 py-3 bg-white/10 border-2 border-white/20 rounded-lg 
                            text-white text-lg placeholder-gray-400 focus:outline-none focus:border-[#ff006e] focus:bg-white/15
                            transition-all duration-300 text-center"
                            required
                        />

                        <AnimatePresence mode="wait">
                            {showPasswordField && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="relative">
                                        <input
                                            type={
                                                showPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            placeholder={
                                                userExists
                                                    ? 'Password'
                                                    : 'Choose a password'
                                            }
                                            className="w-full px-4 py-3 pr-10 bg-white/10 border-2 border-white/20 rounded-lg
                                            text-white text-lg placeholder-gray-400 focus:outline-none focus:border-[#ff006e] focus:bg-white/15
                                            transition-all duration-300 text-center font-mono"
                                            required={showPasswordField}
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white 
                                            transition-colors duration-200 p-1 focus:outline-none"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? (
                                                // Eye with slash (hide password)
                                                <svg
                                                    className="w-5 h-5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                                    />
                                                </svg>
                                            ) : (
                                                // Open eye (show password)
                                                <svg
                                                    className="w-5 h-5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                    />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    {userExists && (
                                        <p className="text-xs text-gray-400 mt-2">
                                            This account already exists. Please
                                            enter your password.
                                        </p>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Terms acceptance checkbox - only show for new users */}
                        <AnimatePresence>
                            {showPasswordField && !userExists && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="mt-4"
                                >
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={termsAccepted}
                                            onChange={(e) =>
                                                setTermsAccepted(
                                                    e.target.checked
                                                )
                                            }
                                            className="hidden"
                                        />
                                        <div className="relative mt-1">
                                            <div
                                                className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                                                    termsAccepted
                                                        ? 'bg-purple-600 border-purple-600'
                                                        : 'bg-transparent border-gray-400 group-hover:border-gray-300'
                                                }`}
                                            >
                                                <AnimatePresence>
                                                    {termsAccepted && (
                                                        <motion.svg
                                                            initial={{
                                                                scale: 0,
                                                                opacity: 0,
                                                            }}
                                                            animate={{
                                                                scale: 1,
                                                                opacity: 1,
                                                            }}
                                                            exit={{
                                                                scale: 0,
                                                                opacity: 0,
                                                            }}
                                                            transition={{
                                                                duration: 0.2,
                                                            }}
                                                            className="w-full h-full p-0.5"
                                                            viewBox="0 0 20 20"
                                                            fill="white"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </motion.svg>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                        <span className="text-xs text-gray-400 leading-relaxed">
                                            By continuing you accept Niio's{' '}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    openPolicyModal(
                                                        'Terms of Use',
                                                        'https://www.niio.com/site/terms/'
                                                    );
                                                }}
                                                className="text-purple-400 hover:text-purple-300 underline transition-colors"
                                            >
                                                Terms of Use
                                            </button>
                                            ,{' '}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    openPolicyModal(
                                                        'Privacy Policy',
                                                        'https://www.niio.com/site/privacy/'
                                                    );
                                                }}
                                                className="text-purple-400 hover:text-purple-300 underline transition-colors"
                                            >
                                                Privacy Policy
                                            </button>
                                            ,{' '}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    openPolicyModal(
                                                        'Cookie Policy',
                                                        'https://www.niio.com/site/cookies/'
                                                    );
                                                }}
                                                className="text-purple-400 hover:text-purple-300 underline transition-colors"
                                            >
                                                Cookie Policy
                                            </button>{' '}
                                            and{' '}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    openPolicyModal(
                                                        'DMCA Policy',
                                                        'https://www.niio.com/site/dmca/'
                                                    );
                                                }}
                                                className="text-purple-400 hover:text-purple-300 underline transition-colors"
                                            >
                                                DMCA Policy
                                            </button>
                                            .
                                        </span>
                                    </label>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {email.trim() !== '' && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                                className="flex justify-center"
                            >
                                <ShimmerButton
                                    type="submit"
                                    disabled={
                                        createUser.isPending ||
                                        signInUser.isPending ||
                                        isLoading ||
                                        isAppleLoading ||
                                        isGoogleLoading ||
                                        (showPasswordField &&
                                            !userExists &&
                                            !termsAccepted)
                                    }
                                    className="w-full max-w-sm"
                                >
                                    {createUser.isPending ||
                                    signInUser.isPending ||
                                    isLoading
                                        ? 'Processing...'
                                        : showPasswordField
                                        ? userExists
                                            ? 'Sign In'
                                            : 'Create Account'
                                        : 'Continue with Email'}
                                </ShimmerButton>
                            </motion.div>
                        )}
                    </form>
                </>
            </div>

            {/* Policy Modal */}
            <PolicyModal
                isOpen={policyModal.isOpen}
                onClose={() =>
                    setPolicyModal({ ...policyModal, isOpen: false })
                }
                title={policyModal.title}
                url={policyModal.url}
            />
        </div>
    );
}
